import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Typography, Spin, Alert, List, Avatar, Space, Divider } from "antd";
import { PlayCircleOutlined, PauseCircleOutlined, StepBackwardOutlined, StepForwardOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";

const { Text } = Typography;

function loadSpotifySdkOnce() {
  if (window.__spotifySdkPromise) return window.__spotifySdkPromise;
  window.__spotifySdkPromise = new Promise((resolve, reject) => {
    if (window.Spotify) return resolve(window.Spotify);
    const existing = document.querySelector('script[src="https://sdk.scdn.co/spotify-player.js"]');
    if (existing) {
      // SDK will call window.onSpotifyWebPlaybackSDKReady when loaded
    } else {
      const script = document.createElement("script");
      script.src = "https://sdk.scdn.co/spotify-player.js";
      script.async = true;
      script.onerror = () => reject(new Error("Failed to load Spotify Web Playback SDK"));
      document.body.appendChild(script);
    }
    window.onSpotifyWebPlaybackSDKReady = () => resolve(window.Spotify);
    // Safety timeout
    setTimeout(() => {
      if (window.Spotify) resolve(window.Spotify);
    }, 12000);
  });
  return window.__spotifySdkPromise;
}

export default function SpotifyTab() {
  const [connectStatus, setConnectStatus] = useState("unknown"); // unknown | not_connected | connected
  const [profile, setProfile] = useState(null);
  const [loadingMe, setLoadingMe] = useState(false);
  const [error, setError] = useState(null);

  const [playerStatus, setPlayerStatus] = useState("idle"); // idle | loading_sdk | connecting | ready | error | premium_required
  const [playerError, setPlayerError] = useState(null);
  const [paused, setPaused] = useState(true);
  const [currentTrack, setCurrentTrack] = useState(null);

  const playerRef = useRef(null);
  const deviceIdRef = useRef(null);
  const tokenCacheRef = useRef({ access_token: null, expires_at: 0 });

  const [query, setQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [trackResults, setTrackResults] = useState([]);

  const [playlistsLoading, setPlaylistsLoading] = useState(false);
  const [playlists, setPlaylists] = useState([]);

  // Axios instance with auth header + 401 refresh-and-retry (mirrors VideoConf.js)
  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: "/api",
      headers: { "Content-Type": "application/json" },
    });
    instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers = config.headers || {};
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
      },
      (err) => Promise.reject(err)
    );
    instance.interceptors.response.use(
      (resp) => resp,
      async (err) => {
        const originalRequest = err.config || {};
        if (err.response && err.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const refreshToken = localStorage.getItem("refresh_token");
            const refreshResponse = await axios.post("/api/refresh_tokens/", { refresh_token: refreshToken });
            if (refreshResponse.status === 200) {
              const { access_token: newToken, refresh_token: newRefreshToken } = refreshResponse.data || {};
              if (newToken) localStorage.setItem("token", newToken);
              if (newRefreshToken) localStorage.setItem("refresh_token", newRefreshToken);
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            // eslint-disable-next-line no-console
            console.error("Failed to refresh token:", refreshError);
          }
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, []);

  const normalizeApiError = useCallback((e) => {
    const code = e?.response?.data?.detail?.code || e?.response?.data?.code;
    const message =
      e?.response?.data?.detail?.message ||
      e?.response?.data?.detail ||
      e?.response?.data?.message ||
      e?.message ||
      "Unknown error";
    return { code, message };
  }, []);

  const fetchMe = useCallback(async () => {
    setLoadingMe(true);
    setError(null);
    try {
      const resp = await axiosInstance.get("/spotify/me");
      const me = resp?.data || null;
      setProfile(me);
      setConnectStatus("connected");
    } catch (e) {
      const { code, message } = normalizeApiError(e);
      if (code === "spotify_not_connected") {
        setConnectStatus("not_connected");
        setProfile(null);
      } else {
        setError(message);
        // Keep state conservative
        setConnectStatus("not_connected");
        setProfile(null);
      }
    } finally {
      setLoadingMe(false);
    }
  }, [axiosInstance, normalizeApiError]);

  useEffect(() => {
    void fetchMe();
  }, [fetchMe]);

  const beginConnect = useCallback(async () => {
    setError(null);
    try {
      const resp = await axiosInstance.get("/spotify/login");
      const authorizeUrl = resp?.data?.authorize_url;
      if (!authorizeUrl) throw new Error("Spotify login URL not returned by backend.");
      window.location.href = authorizeUrl;
    } catch (e) {
      const { message } = normalizeApiError(e);
      setError(message || "Failed to start Spotify login.");
    }
  }, [axiosInstance, normalizeApiError]);

  const getAccessToken = useCallback(async () => {
    // cache for SDK callback (avoid spamming backend)
    const cached = tokenCacheRef.current || {};
    if (cached.access_token && Date.now() < (cached.expires_at || 0) - 15000) {
      return cached.access_token;
    }
    const resp = await axiosInstance.post("/spotify/access-token", {});
    const tok = resp?.data?.access_token;
    const expiresIn = resp?.data?.expires_in || 3600;
    if (!tok) throw new Error("No access token returned.");
    tokenCacheRef.current = {
      access_token: tok,
      expires_at: Date.now() + Number(expiresIn) * 1000,
    };
    return tok;
  }, [axiosInstance]);

  const initPlayer = useCallback(async () => {
    if (playerRef.current) return;
    setPlayerError(null);
    setPlayerStatus("loading_sdk");
    try {
      const Spotify = await loadSpotifySdkOnce();
      if (!Spotify || !Spotify.Player) throw new Error("Spotify Web Playback SDK not available.");

      setPlayerStatus("connecting");
      const player = new Spotify.Player({
        name: "SimplyRide Web Player",
        getOAuthToken: async (cb) => {
          try {
            const tok = await getAccessToken();
            cb(tok);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error("Failed to fetch Spotify access token:", e);
            cb("");
          }
        },
        volume: 0.5,
      });

      player.addListener("initialization_error", ({ message }) => {
        setPlayerStatus("error");
        setPlayerError(message || "Player initialization error.");
      });
      player.addListener("authentication_error", ({ message }) => {
        setPlayerStatus("error");
        setPlayerError(message || "Player authentication error.");
      });
      player.addListener("account_error", ({ message }) => {
        // Typically Premium required
        const msg = message || "Spotify Premium is required for in-browser playback control.";
        setPlayerStatus("premium_required");
        setPlayerError(msg);
      });
      player.addListener("playback_error", ({ message }) => {
        setPlayerStatus("error");
        setPlayerError(message || "Playback error.");
      });

      player.addListener("ready", async ({ device_id }) => {
        deviceIdRef.current = device_id;
        try {
          await axiosInstance.put("/spotify/player/transfer", { device_id });
        } catch (e) {
          // Transfer can fail if Premium missing; surface but don't block browsing
          const { code, message } = normalizeApiError(e);
          if (code === "spotify_premium_required") {
            setPlayerStatus("premium_required");
            setPlayerError("Spotify Premium is required to control playback in the browser.");
          } else {
            setPlayerStatus("error");
            setPlayerError(message || "Failed to transfer playback to web player.");
          }
          return;
        }
        setPlayerStatus("ready");
      });

      player.addListener("not_ready", () => {
        setPlayerStatus("error");
        setPlayerError("Spotify player went offline.");
      });

      player.addListener("player_state_changed", (state) => {
        if (!state) return;
        setPaused(!!state.paused);
        try {
          const t = state.track_window?.current_track;
          if (t) {
            setCurrentTrack({
              name: t.name,
              artists: (t.artists || []).map((a) => a.name).join(", "),
              albumArt: (t.album?.images || [])[0]?.url || null,
            });
          }
        } catch { }
      });

      const connected = await player.connect();
      if (!connected) {
        setPlayerStatus("error");
        setPlayerError("Failed to connect Spotify player.");
        return;
      }

      playerRef.current = player;
    } catch (e) {
      setPlayerStatus("error");
      setPlayerError(e?.message || "Failed to initialize Spotify player.");
    }
  }, [axiosInstance, getAccessToken, normalizeApiError]);

  useEffect(() => {
    // Auto-init player when connected
    if (connectStatus === "connected") {
      void initPlayer();
    }
  }, [connectStatus, initPlayer]);

  useEffect(() => {
    return () => {
      try {
        playerRef.current?.disconnect?.();
      } catch { }
      playerRef.current = null;
      deviceIdRef.current = null;
    };
  }, []);

  const refreshPlaylists = useCallback(async () => {
    setPlaylistsLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get("/spotify/playlists");
      const items = resp?.data?.items || [];
      setPlaylists(Array.isArray(items) ? items : []);
    } catch (e) {
      const { code, message } = normalizeApiError(e);
      if (code === "spotify_not_connected") {
        setConnectStatus("not_connected");
        setProfile(null);
      } else {
        setError(message);
      }
    } finally {
      setPlaylistsLoading(false);
    }
  }, [axiosInstance, normalizeApiError]);

  useEffect(() => {
    if (connectStatus === "connected") void refreshPlaylists();
  }, [connectStatus, refreshPlaylists]);

  const doSearch = useCallback(async () => {
    const q = (query || "").trim();
    if (!q) return;
    setSearchLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get("/spotify/search", {
        params: { q, type: "track", limit: 20 },
      });
      const items = resp?.data?.tracks?.items || [];
      setTrackResults(Array.isArray(items) ? items : []);
    } catch (e) {
      const { code, message } = normalizeApiError(e);
      if (code === "spotify_not_connected") {
        setConnectStatus("not_connected");
        setProfile(null);
      } else {
        setError(message);
      }
    } finally {
      setSearchLoading(false);
    }
  }, [axiosInstance, normalizeApiError, query]);

  const playTrack = useCallback(
    async (uri) => {
      setError(null);
      try {
        await axiosInstance.put("/spotify/player/play", {
          uris: [uri],
          device_id: deviceIdRef.current || undefined,
        });
      } catch (e) {
        const { code, message } = normalizeApiError(e);
        if (code === "spotify_premium_required") {
          setPlayerStatus("premium_required");
          setPlayerError("Spotify Premium is required to control playback in the browser.");
          return;
        }
        setError(message);
      }
    },
    [axiosInstance, normalizeApiError]
  );

  const playPlaylist = useCallback(
    async (contextUri) => {
      setError(null);
      try {
        await axiosInstance.put("/spotify/player/play", {
          context_uri: contextUri,
          device_id: deviceIdRef.current || undefined,
        });
      } catch (e) {
        const { code, message } = normalizeApiError(e);
        if (code === "spotify_premium_required") {
          setPlayerStatus("premium_required");
          setPlayerError("Spotify Premium is required to control playback in the browser.");
          return;
        }
        setError(message);
      }
    },
    [axiosInstance, normalizeApiError]
  );

  const pauseOrResume = useCallback(async () => {
    setError(null);
    try {
      if (paused) {
        await axiosInstance.put("/spotify/player/play", { device_id: deviceIdRef.current || undefined });
      } else {
        await axiosInstance.put("/spotify/player/pause", { device_id: deviceIdRef.current || undefined });
      }
    } catch (e) {
      const { code, message } = normalizeApiError(e);
      if (code === "spotify_premium_required") {
        setPlayerStatus("premium_required");
        setPlayerError("Spotify Premium is required to control playback in the browser.");
        return;
      }
      setError(message);
    }
  }, [axiosInstance, normalizeApiError, paused]);

  const nextTrack = useCallback(async () => {
    setError(null);
    try {
      await axiosInstance.post("/spotify/player/next", { device_id: deviceIdRef.current || undefined });
    } catch (e) {
      const { code, message } = normalizeApiError(e);
      if (code === "spotify_premium_required") {
        setPlayerStatus("premium_required");
        setPlayerError("Spotify Premium is required to control playback in the browser.");
        return;
      }
      setError(message);
    }
  }, [axiosInstance, normalizeApiError]);

  const prevTrack = useCallback(async () => {
    setError(null);
    try {
      await axiosInstance.post("/spotify/player/previous", { device_id: deviceIdRef.current || undefined });
    } catch (e) {
      const { code, message } = normalizeApiError(e);
      if (code === "spotify_premium_required") {
        setPlayerStatus("premium_required");
        setPlayerError("Spotify Premium is required to control playback in the browser.");
        return;
      }
      setError(message);
    }
  }, [axiosInstance, normalizeApiError]);

  const connectedAs = profile?.display_name || profile?.id || null;
  const albumArt = currentTrack?.albumArt;

  return (
    <div style={{ padding: 12 }}>
      <Space direction="vertical" style={{ width: "100%" }} size={10}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text strong>Spotify</Text>
            {connectStatus === "connected" && connectedAs ? (
              <Text type="secondary">Connected as {connectedAs}</Text>
            ) : connectStatus === "not_connected" ? (
              <Text type="secondary">Not connected</Text>
            ) : (
              <Text type="secondary">Checking connection…</Text>
            )}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            {connectStatus !== "connected" ? (
              <Button type="primary" className="primary-glass-btn" onClick={beginConnect} disabled={loadingMe}>
                Connect Spotify
              </Button>
            ) : (
              <Button onClick={fetchMe} icon={<ReloadOutlined />} disabled={loadingMe}>
                Refresh
              </Button>
            )}
          </div>
        </div>

        {error ? <Alert type="error" showIcon message={error} /> : null}

        {connectStatus === "connected" ? (
          <>
            <div className="glass-panel glass-section" style={{ padding: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {albumArt ? <Avatar shape="square" size={52} src={albumArt} /> : <Avatar shape="square" size={52} />}
                  <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                    <Text strong>{currentTrack?.name || "Nothing playing yet"}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {currentTrack?.artists || "-"}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Player: {playerStatus === "ready" ? "Ready" : playerStatus === "loading_sdk" ? "Loading SDK…" : playerStatus === "connecting" ? "Connecting…" : playerStatus === "premium_required" ? "Premium required" : playerStatus}
                    </Text>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button icon={<StepBackwardOutlined />} onClick={prevTrack} disabled={connectStatus !== "connected"} />
                  <Button
                    type="primary"
                    icon={paused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
                    onClick={pauseOrResume}
                    disabled={playerStatus !== "ready"}
                  >
                    {paused ? "Play" : "Pause"}
                  </Button>
                  <Button icon={<StepForwardOutlined />} onClick={nextTrack} disabled={connectStatus !== "connected"} />
                </div>
              </div>

              {playerError ? (
                <div style={{ marginTop: 10 }}>
                  <Alert
                    type={playerStatus === "premium_required" ? "warning" : "error"}
                    showIcon
                    message={playerError}
                    description={
                      playerStatus === "premium_required"
                        ? "You can still browse/search playlists and tracks, but in-browser playback control requires Spotify Premium."
                        : null
                    }
                  />
                </div>
              ) : null}
            </div>

            <Divider style={{ margin: "10px 0" }} />

            <div className="glass-panel glass-section" style={{ padding: 12 }}>
              <Text strong>Search tracks</Text>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <Input
                  placeholder="Search tracks (e.g., Daft Punk harder better)"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onPressEnter={doSearch}
                />
                <Button type="primary" onClick={doSearch} disabled={!query.trim()} loading={searchLoading}>
                  Search
                </Button>
              </div>

              <div style={{ marginTop: 10 }}>
                {searchLoading ? (
                  <Spin />
                ) : (
                  <List
                    size="small"
                    dataSource={trackResults}
                    locale={{ emptyText: "No results yet." }}
                    renderItem={(t) => {
                      const img = (t?.album?.images || [])[t?.album?.images?.length - 1]?.url || null;
                      const name = t?.name || "Track";
                      const artists = (t?.artists || []).map((a) => a.name).join(", ");
                      return (
                        <List.Item
                          actions={[
                            <Button key="play" type="link" onClick={() => playTrack(t?.uri)} disabled={!t?.uri || playerStatus !== "ready"}>
                              Play
                            </Button>,
                          ]}
                        >
                          <List.Item.Meta
                            avatar={img ? <Avatar shape="square" size={40} src={img} /> : <Avatar shape="square" size={40} />}
                            title={<span>{name}</span>}
                            description={<span style={{ color: "rgba(0,0,0,0.55)" }}>{artists}</span>}
                          />
                        </List.Item>
                      );
                    }}
                  />
                )}
              </div>
            </div>

            <Divider style={{ margin: "10px 0" }} />

            <div className="glass-panel glass-section" style={{ padding: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Text strong>Your playlists</Text>
                <Button icon={<ReloadOutlined />} onClick={refreshPlaylists} loading={playlistsLoading}>
                  Refresh
                </Button>
              </div>

              <div style={{ marginTop: 10 }}>
                {playlistsLoading ? (
                  <Spin />
                ) : (
                  <List
                    size="small"
                    dataSource={playlists}
                    locale={{ emptyText: "No playlists found." }}
                    renderItem={(p) => {
                      const img = (p?.images || [])[p?.images?.length - 1]?.url || null;
                      return (
                        <List.Item
                          actions={[
                            <Button key="play" type="link" onClick={() => playPlaylist(p?.uri)} disabled={!p?.uri || playerStatus !== "ready"}>
                              Play
                            </Button>,
                          ]}
                        >
                          <List.Item.Meta
                            avatar={img ? <Avatar shape="square" size={40} src={img} /> : <Avatar shape="square" size={40} />}
                            title={<span>{p?.name || "Playlist"}</span>}
                            description={<span style={{ color: "rgba(0,0,0,0.55)" }}>{p?.owner?.display_name || ""}</span>}
                          />
                        </List.Item>
                      );
                    }}
                  />
                )}
              </div>
            </div>
          </>
        ) : connectStatus === "unknown" ? (
          <Spin />
        ) : (
          <Alert
            type="info"
            showIcon
            message="Connect Spotify to enable search, playlists, and in-browser playback controls."
            description="Note: In-browser playback control requires Spotify Premium. If you don’t have Premium, you can still browse/search."
          />
        )}
      </Space>
    </div>
  );
}


